
import { useState, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { useIsMobile } from "@/hooks/use-mobile";
import { usePageTracking } from "@/hooks/usePageTracking";
import Index from "./pages/Index";
import Thales from "./pages/Thales";
import Danone from "./pages/Danone";
import NotFound from "./pages/NotFound";
import Destinations from "./pages/Destinations"; 
import EventIdeas from "./pages/EventIdeas";
import AboutUs from "./pages/AboutUs";
import Tools from "./pages/Tools";
import Suggestions from "./pages/Suggestions";
import ScrollToTop from "./components/ScrollToTop";
import HubSpotChatManager from "./components/HubSpotChatManager";

const queryClient = new QueryClient();

const AppRoutes = () => {
  usePageTracking();
  
  return (
    <Routes>
      <Route path="/" element={<Danone />} />
      <Route path="/thales" element={<Thales />} />
      <Route path="/destinations" element={<Destinations />} /> 
      <Route path="/event-ideas" element={<EventIdeas />} />
      <Route path="/about-us" element={<AboutUs />} />
      <Route path="/tools" element={<Tools />} />
      <Route path="/suggestions" element={<Suggestions />} />
      <Route path="/suggestionsAI" element={<Suggestions />} />
      {/* AJOUTEZ TOUTES LES ROUTES PERSONNALISÉES AU-DESSUS DE LA ROUTE CATCH-ALL "*" */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

const App = () => {
  const [cookiesHandled, setCookiesHandled] = useState(false);
  const isMobile = useIsMobile();

  // Wait for cookie popup to be handled
  useEffect(() => {
    const waitForCookies = () => {
      // Check if Axeptio widget exists and is visible
      const checkCookieStatus = () => {
        const axeptioWidget = document.querySelector('[data-axeptio-widget]') as HTMLElement;
        const axeptioModal = document.querySelector('.axeptio_widget') as HTMLElement;
        
        // If no cookie widgets found, cookies are handled
        if (!axeptioWidget && !axeptioModal) {
          setCookiesHandled(true);
          return;
        }
        
        // If widgets exist but are hidden, cookies are handled
        if ((axeptioWidget && axeptioWidget.style.display === 'none') || 
            (axeptioModal && axeptioModal.style.display === 'none')) {
          setCookiesHandled(true);
          return;
        }
        
        // If widgets are visible, keep checking
        setTimeout(checkCookieStatus, 500);
      };
      
      // Start checking after a short delay to let Axeptio load
      setTimeout(checkCookieStatus, 2000);
    };

    waitForCookies();

    // Fallback: if no cookie interaction after 20 seconds, mark as handled anyway
    const fallbackTimer = setTimeout(() => {
      setCookiesHandled(true);
    }, 20000);

    return () => clearTimeout(fallbackTimer);
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <ScrollToTop />
          <HubSpotChatManager />
          <AppRoutes />
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
